import type { SerializedNode, ConversionContext, RGBA, Gradient } from '../types';
import {
  ANIMATION_TYPES,
  ANIMATION_EASINGS,
  HOVER_EFFECT_TYPES,
  IMAGE_CONTINUOUS_TYPES,
  type AnimationType,
  type AnimationEasing,
  type HoverEffectType,
  type ImageContinuousType,
} from '../../types/animation';
import { getAttribute } from '../parser';
import {
  parseColor,
  parsePadding,
  parseBorderRadius,
  parseBoolean,
  parseInteger,
  parseFloat_,
  parsePosition,
  parsePositionOffset,
  inferPositionAnchorsState,
  parseShadowAttributes,
  SHADOW_ATTRIBUTES,
} from '../utils';
import { IMAGE_DEFAULTS, getLeafFillSpace } from '../utils/defaults';
import { IMAGE_NODE_NAME, EXTERNAL_LINK_EVENT, NO_LINK_EVENT } from '../../utils/constants';
import { checkIsGradient } from '../../utils/styleHelpers';

/**
 * Transform an <image> XML element into a CraftJS Image node
 *
 * @param element - The XML element
 * @param context - Conversion context
 * @returns SerializedNode for the Image
 */
export const transformImage = (element: Element, context: ConversionContext): SerializedNode => {
  // Parse required src
  const srcAttr = getAttribute(element, 'src');
  const src = srcAttr || '';

  // Parse alt text
  const altAttr = getAttribute(element, 'alt');
  const alt = altAttr || IMAGE_DEFAULTS.alt;

  // Parse fit type (contain, cover, fill, tile)
  const fitAttr = getAttribute(element, 'fit');
  const fitType = mapFitType(fitAttr) || IMAGE_DEFAULTS.fitType;

  // Parse dimensions
  const widthAttr = getAttribute(element, 'width');
  const width = widthAttr || IMAGE_DEFAULTS.width;

  const heightAttr = getAttribute(element, 'height');
  const height = heightAttr || IMAGE_DEFAULTS.height;

  // Parse maxWidth with unit
  const maxWidthAttr = getAttribute(element, 'maxWidth');
  const { value: maxWidth, unit: maxWidthUnit } = parseValueWithUnit(
    maxWidthAttr,
    IMAGE_DEFAULTS.maxWidth,
    IMAGE_DEFAULTS.maxWidthUnit,
  );

  // Parse objectPosition
  const objectPositionAttr = getAttribute(element, 'objectPosition');
  const objectPosition = objectPositionAttr || IMAGE_DEFAULTS.objectPosition;

  // Parse click behavior
  const clickUrlAttr = getAttribute(element, 'clickUrl');
  const openInNewTabAttr = getAttribute(element, 'openInNewTab');
  const openInNewTab = parseBoolean(openInNewTabAttr, false);
  const clickEvent = buildClickEvent(clickUrlAttr, openInNewTab);

  // Parse spacing
  const paddingAttr = getAttribute(element, 'padding');
  const padding = parsePadding(paddingAttr, IMAGE_DEFAULTS.padding);

  const marginAttr = getAttribute(element, 'margin');
  const margin = parsePadding(marginAttr, IMAGE_DEFAULTS.margin);

  // Parse border
  const borderRadiusAttr = getAttribute(element, 'borderRadius');
  const borderRadius = parseBorderRadius(borderRadiusAttr, IMAGE_DEFAULTS.borderRadius);

  const borderWidthAttr = getAttribute(element, 'borderWidth');
  const borderWidth = parseInteger(borderWidthAttr, IMAGE_DEFAULTS.borderWidth);

  const borderColorAttr = getAttribute(element, 'borderColor');
  const borderColor = borderColorAttr ? parseColor(borderColorAttr) : undefined;

  const hoverBorderColorAttr = getAttribute(element, 'hoverBorderColor');
  const hoverBorderColor = hoverBorderColorAttr ? parseColor(hoverBorderColorAttr) : undefined;

  // Parse opacity
  const opacityAttr = getAttribute(element, 'opacity');
  const opacity = parseFloat_(opacityAttr, IMAGE_DEFAULTS.opacity);

  // Parse background color
  const bgAttr = getAttribute(element, 'bg');
  const backgroundColor = bgAttr ? parseColor(bgAttr) : undefined;

  // Parse position attributes
  const positionAttr = getAttribute(element, 'position');
  const position = parsePosition(positionAttr) || IMAGE_DEFAULTS.position;

  const topAttr = getAttribute(element, 'top');
  const top = parsePositionOffset(topAttr);

  const leftAttr = getAttribute(element, 'left');
  const left = parsePositionOffset(leftAttr);

  const rightAttr = getAttribute(element, 'right');
  const right = parsePositionOffset(rightAttr);

  const bottomAttr = getAttribute(element, 'bottom');
  const bottom = parsePositionOffset(bottomAttr);

  const zIndexAttr = getAttribute(element, 'zIndex');
  const zIndex = parseInteger(zIndexAttr, IMAGE_DEFAULTS.zIndex);

  // Infer position anchors state from provided values
  const positionAnchorsState = inferPositionAnchorsState(position, top, left, right, bottom);

  // Parse shadow attributes
  const shadow = parseShadowAttributes(element);

  // Parse breakpoint overrides
  const tablet = parseBreakpointOverrides(element, 'tablet');
  const mobile = parseBreakpointOverrides(element, 'mobile');

  // Build props object
  const props: Record<string, unknown> = {
    src,
    alt,
    fitType,
    width,
    height,
    padding,
    margin,
    borderRadius,
    borderWidth,
    opacity,
    clickEvent,
    objectPosition,
    backgroundPosition: objectPosition,
    backgroundSize: IMAGE_DEFAULTS.backgroundSize,
    position,
    top: top || IMAGE_DEFAULTS.top,
    left: left || IMAGE_DEFAULTS.left,
    right: right || IMAGE_DEFAULTS.right,
    bottom: bottom || IMAGE_DEFAULTS.bottom,
    positionAnchorsState,
    zIndex,
    minWidth: IMAGE_DEFAULTS.minWidth,
    minHeight: IMAGE_DEFAULTS.minHeight,
    maxWidth,
    maxWidthUnit,
    fillSpace: getImageFillSpace(widthAttr, context.parentFlexDirection),
    flexBasis: IMAGE_DEFAULTS.flexBasis,
    flexGrow: IMAGE_DEFAULTS.flexGrow,
    visibility: IMAGE_DEFAULTS.visibility,
    aspectRatioLock: IMAGE_DEFAULTS.aspectRatioLock,
    aspectRatio: IMAGE_DEFAULTS.aspectRatio,
    tablet: { ...IMAGE_DEFAULTS.tablet, ...tablet },
    mobile: { ...IMAGE_DEFAULTS.mobile, ...mobile },
  };

  // Only include optional props if they have values
  if (borderColor) {
    props.borderColor = borderColor;
  }
  if (hoverBorderColor) {
    props.hoverBorderColor = hoverBorderColor;
  }
  if (backgroundColor) {
    props.backgroundColor = backgroundColor;
    // Set bgGradient or bgSolid for inspector panel color field
    if (typeof backgroundColor !== 'string') {
      if (checkIsGradient(backgroundColor)) {
        props.bgGradient = backgroundColor as Gradient;
      } else {
        props.bgSolid = backgroundColor as RGBA;
      }
    }
  }
  if (shadow) {
    props.shadowType = shadow.shadowType;
    props.shadowSettings = shadow.shadowSettings;
  }

  // Parse animation attributes
  const animationProps = parseImageAnimationAttributes(element);
  Object.assign(props, animationProps);

  return {
    type: { resolvedName: IMAGE_NODE_NAME },
    isCanvas: false,
    props,
    displayName: IMAGE_NODE_NAME,
    custom: {
      displayName: IMAGE_NODE_NAME,
      isLocked: false,
    },
    hidden: false,
    nodes: [],
    linkedNodes: {},
    parent: context.parentId,
  };
};

/**
 * Map fit attribute to CraftJS fitType value
 */
const mapFitType = (
  value: string | null,
): 'contain' | 'cover' | 'fill' | 'tile' | 'none' | 'scale-down' | undefined => {
  if (!value) return undefined;

  const normalized = value.toLowerCase().trim();
  switch (normalized) {
    case 'contain':
      return 'contain';
    case 'cover':
      return 'cover';
    case 'fill':
      return 'fill';
    case 'tile':
      return 'tile';
    case 'none':
      return 'none';
    case 'scale-down':
      return 'scale-down';
    default:
      return undefined;
  }
};

/**
 * Determine fillSpace for image based on explicit width and parent direction.
 *
 * When an explicit width is set (and is not 100%), we should NOT fill space
 * to respect the explicit sizing. Otherwise, use parent-aware logic.
 */
const getImageFillSpace = (
  explicitWidth: string | null,
  parentFlexDirection: 'row' | 'column' | undefined,
): 'yes' | 'no' => {
  // If explicit width is set and is not 100%, don't fill (respect the fixed size)
  if (explicitWidth && explicitWidth !== '100%') {
    return 'no';
  }
  // Otherwise use parent-aware logic
  return getLeafFillSpace(parentFlexDirection, IMAGE_DEFAULTS.fillSpace);
};

/**
 * Build a click event object for the image
 */
const buildClickEvent = (url: string | null, openInNewTab: boolean) => {
  if (!url) {
    return {
      eventType: NO_LINK_EVENT,
      eventData: null,
    };
  }

  return {
    eventType: EXTERNAL_LINK_EVENT,
    eventData: {
      url,
      openInNewTab,
    },
  };
};

/**
 * Parse a value with optional unit suffix (px or %)
 */
const parseValueWithUnit = (
  value: string | null,
  defaultValue: number,
  defaultUnit: 'px' | '%',
): { value: number; unit: 'px' | '%' } => {
  if (!value) {
    return { value: defaultValue, unit: defaultUnit };
  }

  const trimmed = value.trim();
  if (trimmed.endsWith('%')) {
    const num = parseFloat(trimmed.slice(0, -1));
    return { value: isNaN(num) ? defaultValue : num, unit: '%' };
  }

  // Strip 'px' suffix if present, then parse as integer
  const numStr = trimmed.endsWith('px') ? trimmed.slice(0, -2) : trimmed;
  const num = parseInt(numStr, 10);
  return { value: isNaN(num) ? defaultValue : num, unit: 'px' };
};

/**
 * Parse breakpoint-specific overrides (tablet-* or mobile-* prefixed attributes)
 */
const parseBreakpointOverrides = (
  element: Element,
  breakpoint: 'tablet' | 'mobile',
): Record<string, unknown> => {
  const overrides: Record<string, unknown> = {};

  // width
  const widthAttr = getAttribute(element, `${breakpoint}-width`);
  if (widthAttr) overrides.width = widthAttr;

  // height
  const heightAttr = getAttribute(element, `${breakpoint}-height`);
  if (heightAttr) overrides.height = heightAttr;

  // maxWidth (with unit)
  const maxWidthAttr = getAttribute(element, `${breakpoint}-maxWidth`);
  if (maxWidthAttr) {
    const { value, unit } = parseValueWithUnit(
      maxWidthAttr,
      IMAGE_DEFAULTS.maxWidth,
      IMAGE_DEFAULTS.maxWidthUnit,
    );
    overrides.maxWidth = value;
    overrides.maxWidthUnit = unit;
  }

  // padding
  const paddingAttr = getAttribute(element, `${breakpoint}-padding`);
  if (paddingAttr) overrides.padding = parsePadding(paddingAttr, [0, 0, 0, 0]);

  // margin
  const marginAttr = getAttribute(element, `${breakpoint}-margin`);
  if (marginAttr) overrides.margin = parsePadding(marginAttr, [0, 0, 0, 0]);

  // borderRadius
  const borderRadiusAttr = getAttribute(element, `${breakpoint}-borderRadius`);
  if (borderRadiusAttr)
    overrides.borderRadius = parseBorderRadius(borderRadiusAttr, IMAGE_DEFAULTS.borderRadius);

  // Position attributes
  const positionAttr = getAttribute(element, `${breakpoint}-position`);
  if (positionAttr) {
    const parsedPosition = parsePosition(positionAttr);
    if (parsedPosition) overrides.position = parsedPosition;
  }

  const topAttr = getAttribute(element, `${breakpoint}-top`);
  const leftAttr = getAttribute(element, `${breakpoint}-left`);
  const rightAttr = getAttribute(element, `${breakpoint}-right`);
  const bottomAttr = getAttribute(element, `${breakpoint}-bottom`);

  const top = parsePositionOffset(topAttr);
  const left = parsePositionOffset(leftAttr);
  const right = parsePositionOffset(rightAttr);
  const bottom = parsePositionOffset(bottomAttr);

  if (top !== undefined) overrides.top = top;
  if (left !== undefined) overrides.left = left;
  if (right !== undefined) overrides.right = right;
  if (bottom !== undefined) overrides.bottom = bottom;

  // Infer positionAnchorsState if any position props are provided
  const hasPositionOverride = positionAttr || topAttr || leftAttr || rightAttr || bottomAttr;
  if (hasPositionOverride) {
    const breakpointPosition = parsePosition(positionAttr);
    overrides.positionAnchorsState = inferPositionAnchorsState(
      breakpointPosition,
      top,
      left,
      right,
      bottom,
    );
  }

  const zIndexAttr = getAttribute(element, `${breakpoint}-zIndex`);
  if (zIndexAttr) overrides.zIndex = parseInteger(zIndexAttr, 0);

  return overrides;
};

/**
 * Parse animation, hover effect, and continuous animation attributes from an image element
 */
const parseImageAnimationAttributes = (element: Element): Record<string, unknown> => {
  const props: Record<string, unknown> = {};

  const animationTypeAttr = getAttribute(element, 'animationType');
  if (animationTypeAttr && (ANIMATION_TYPES as readonly string[]).includes(animationTypeAttr)) {
    props.animationType = animationTypeAttr as AnimationType;
  }

  const animationDurationAttr = getAttribute(element, 'animationDuration');
  if (animationDurationAttr) {
    props.animationDuration = parseInteger(animationDurationAttr, 600);
  }

  const animationDelayAttr = getAttribute(element, 'animationDelay');
  if (animationDelayAttr) {
    props.animationDelay = parseInteger(animationDelayAttr, 0);
  }

  const animationEasingAttr = getAttribute(element, 'animationEasing');
  if (
    animationEasingAttr &&
    (ANIMATION_EASINGS as readonly string[]).includes(animationEasingAttr)
  ) {
    props.animationEasing = animationEasingAttr as AnimationEasing;
  }

  const hoverEffectAttr = getAttribute(element, 'hoverEffect');
  if (hoverEffectAttr && (HOVER_EFFECT_TYPES as readonly string[]).includes(hoverEffectAttr)) {
    props.hoverEffect = hoverEffectAttr as HoverEffectType;
  }

  const hoverTransitionDurationAttr = getAttribute(element, 'hoverTransitionDuration');
  if (hoverTransitionDurationAttr) {
    props.hoverTransitionDuration = parseInteger(hoverTransitionDurationAttr, 300);
  }

  const hoverIntensityAttr = getAttribute(element, 'hoverIntensity');
  if (hoverIntensityAttr) {
    props.hoverIntensity = parseInteger(hoverIntensityAttr, 105);
  }

  const continuousAnimationAttr = getAttribute(element, 'continuousAnimation');
  if (
    continuousAnimationAttr &&
    (IMAGE_CONTINUOUS_TYPES as readonly string[]).includes(continuousAnimationAttr)
  ) {
    props.continuousAnimation = continuousAnimationAttr as ImageContinuousType;
  }

  const continuousSpeedAttr = getAttribute(element, 'continuousSpeed');
  if (continuousSpeedAttr) {
    props.continuousSpeed = parseInteger(continuousSpeedAttr, 15);
  }

  const continuousIntensityAttr = getAttribute(element, 'continuousIntensity');
  if (continuousIntensityAttr) {
    props.continuousIntensity = parseInteger(continuousIntensityAttr, 10);
  }

  return props;
};

/**
 * Valid attributes for image elements
 */
export const IMAGE_ATTRIBUTES = [
  'src',
  'alt',
  'fit',
  'width',
  'height',
  'padding',
  'margin',
  'borderRadius',
  'borderWidth',
  'borderColor',
  'hoverBorderColor',
  'clickUrl',
  'openInNewTab',
  'opacity',
  'bg',
  'objectPosition',
  'maxWidth',
  // Shadow attributes
  ...SHADOW_ATTRIBUTES,
  // Position attributes
  'position',
  'top',
  'left',
  'right',
  'bottom',
  'zIndex',
  // Animation attributes
  'animationType',
  'animationDuration',
  'animationDelay',
  'animationEasing',
  'hoverEffect',
  'hoverTransitionDuration',
  'hoverIntensity',
  'continuousAnimation',
  'continuousSpeed',
  'continuousIntensity',
  // Tablet breakpoint overrides
  'tablet-width',
  'tablet-height',
  'tablet-maxWidth',
  'tablet-padding',
  'tablet-margin',
  'tablet-borderRadius',
  'tablet-position',
  'tablet-top',
  'tablet-left',
  'tablet-right',
  'tablet-bottom',
  'tablet-zIndex',
  // Mobile breakpoint overrides
  'mobile-width',
  'mobile-height',
  'mobile-maxWidth',
  'mobile-padding',
  'mobile-margin',
  'mobile-borderRadius',
  'mobile-position',
  'mobile-top',
  'mobile-left',
  'mobile-right',
  'mobile-bottom',
  'mobile-zIndex',
] as const;

/**
 * Check if an attribute is valid for image elements
 */
export const isValidImageAttribute = (attr: string): boolean => {
  return (IMAGE_ATTRIBUTES as readonly string[]).includes(attr);
};
